/**
 * useStreamActivity
 *
 * Stream-activity state machine extracted verbatim from useChat.ts (Stage 3).
 * Owns the streamActivity timeline state + ref + the push/pushThinking/clear
 * operations. Behavior is byte-identical to the in-line implementation:
 *  - ref-then-setState ordering is preserved on every mutation,
 *  - the setTimeout requeue in pushThinkingStatus is preserved,
 *  - pushActivity / clearActivity stay useCallback([]) (stable identity),
 *  - pushThinkingStatus stays a plain (non-memoized) closure,
 *  - streamActivityRef stays the SAME useRef (returned for the session-switch
 *    reset sites + the sendMessage read site that live in the orchestrator).
 */
import { useState, useCallback, useRef } from 'react'
import type { StreamActivityStep } from './useChat.types'
import {
  isGenericThinkingLabel,
  normalizeActivityKey,
  makeActivityId,
} from '../utils/stream-text'

export interface UseStreamActivityReturn {
  streamActivity: StreamActivityStep[]
  streamActivityRef: React.MutableRefObject<StreamActivityStep[]>
  setStreamActivity: React.Dispatch<React.SetStateAction<StreamActivityStep[]>>
  pushActivity: (label: string, type: StreamActivityStep['type']) => void
  pushThinkingStatus: (label: string) => void
  clearActivity: () => void
}

export function useStreamActivity(): UseStreamActivityReturn {
  const [streamActivity, setStreamActivity] = useState<StreamActivityStep[]>([])
  const streamActivityRef = useRef<StreamActivityStep[]>([])

  /** Append a step to the activity timeline (uses ref to avoid stale closures) */
  const pushActivity = useCallback((label: string, type: StreamActivityStep['type']) => {
    const step: StreamActivityStep = { id: makeActivityId(), label, type, timestamp: Date.now() }
    const prev = streamActivityRef.current
    // Dedupe: skip if last step has same label (regardless of type — prevents
    // "Setting up..." appearing as both a thinking step and a tool_start step)
    const last = prev[prev.length - 1]
    if (last && last.label === label) return
    // When a concrete step (tool_start etc.) arrives, replace any trailing
    // thinking step so we don't stack duplicate statuses together.
    if (type !== 'thinking' && last && last.type === 'thinking') {
      const next = [...prev.slice(0, -1), step]
      streamActivityRef.current = next
      setStreamActivity(next)
      return
    }
    // Cap at 25 steps to avoid unbounded growth
    const next = [...prev.slice(-24), step]
    streamActivityRef.current = next
    setStreamActivity(next)
  }, [])

  /**
   * Upsert thinking step. Concrete work statuses (image search, section build,
   * brand-guide load, etc.) APPEND to the timeline so the user sees what the
   * agent actually did. Generic placeholders ("Thinking...", "Working on it...")
   * replace the last placeholder in-place to avoid spam.
   */
  const MIN_STATUS_DISPLAY_MS = 1500 // minimum time a generic placeholder stays visible
  // Concrete statuses (the ones the user actually reads) get a shorter
  // floor: they're fine to append fast, but a REPLACEMENT that would wipe
  // a still-fresh concrete status off the tail is blocked for 600ms so
  // tool-bursty backends don't flash a label past unreadably.
  const MIN_CONCRETE_STATUS_MS = 600

  const pushThinkingStatus = (label: string) => {
    const trimmed = (label || '').trim()
    if (!trimmed) return

    const prev = streamActivityRef.current
    const isIncomingGeneric = isGenericThinkingLabel(trimmed)

    // Phrase-level dedup: `Building header...` and `Building the header
    // section...` should count as the same step. Strip articles, "section",
    // and punctuation/whitespace variation before comparing.
    const incomingKey = normalizeActivityKey(trimmed)
    // Re-emitted same key: don't suppress it here; fall through to the
    // re-promote branch below so we bump its timestamp and move it to the
    // tail of the timeline. Skipping would hide the fact that the agent is
    // still working on that section.
    const existingSameKeyIdx = prev.findIndex((s: StreamActivityStep) => normalizeActivityKey(s.label) === incomingKey)

    const lastThinkingIdx = (prev as StreamActivityStep[] & {
      findLastIndex(predicate: (s: StreamActivityStep) => boolean): number
    }).findLastIndex((s: StreamActivityStep) => s.type === 'thinking')
    const lastThinking = lastThinkingIdx >= 0 ? prev[lastThinkingIdx] : null

    if (isIncomingGeneric) {
      // Re-emitted generic with a key already present: ignore.
      if (existingSameKeyIdx >= 0) return
      // Generic placeholders never sit on top of concrete work. If the latest
      // thinking step is concrete, drop the incoming generic entirely.
      if (lastThinking && !isGenericThinkingLabel(lastThinking.label)) return
      // Don't append a generic placeholder after a tool step.
      const last = prev[prev.length - 1]
      if (last && last.type !== 'thinking') return

      // Minimum display time for a generic placeholder: don't replace one
      // that's been showing for less than 1.5s — the user can't read it
      // that fast. Queue the replacement after the remaining time.
      if (lastThinking) {
        const elapsed = Date.now() - (lastThinking.timestamp || 0)
        if (elapsed < MIN_STATUS_DISPLAY_MS) {
          const remaining = MIN_STATUS_DISPLAY_MS - elapsed
          setTimeout(() => pushThinkingStatus(trimmed), remaining)
          return
        }
      }

      // Replace the existing generic placeholder in-place.
      if (lastThinking) {
        const updated = [...prev]
        updated[lastThinkingIdx] = {
          ...updated[lastThinkingIdx],
          label: trimmed,
          timestamp: Date.now(),
        }
        streamActivityRef.current = updated
        setStreamActivity(updated)
        return
      }
      // No existing placeholder — append a new one.
      pushActivity(trimmed, 'thinking')
      return
    }

    // Re-emitted concrete key: re-promote to the tail so the user sees a
    // "still working" cue for iterative same-section edits. Bump the
    // timestamp so the min-display-time rules treat it as fresh.
    if (existingSameKeyIdx >= 0) {
      const existing = prev[existingSameKeyIdx]
      if (existingSameKeyIdx === prev.length - 1) {
        // Already at the tail — bump its timestamp in place.
        const updated = [...prev]
        updated[existingSameKeyIdx] = { ...existing, timestamp: Date.now() }
        streamActivityRef.current = updated
        setStreamActivity(updated)
        return
      }
      const updated = [
        ...prev.slice(0, existingSameKeyIdx),
        ...prev.slice(existingSameKeyIdx + 1),
        { ...existing, timestamp: Date.now() },
      ]
      streamActivityRef.current = updated
      setStreamActivity(updated)
      return
    }

    // Concrete work status. APPEND to the timeline so the user can see the
    // sequence of what the agent actually did. If the latest thinking step
    // is a generic placeholder, replace it instead (the generic is
    // superseded by the concrete one).
    if (lastThinking && isGenericThinkingLabel(lastThinking.label)) {
      // Replacement: gate on MIN_CONCRETE_STATUS_MS relative to the generic
      // tail's display time. The generic is cheap to overwrite, but the
      // min-display floor is symmetric with the one below so a rapid
      // concrete-on-concrete burst through this path is still readable.
      const elapsed = Date.now() - (lastThinking.timestamp || 0)
      if (elapsed < MIN_CONCRETE_STATUS_MS) {
        const remaining = MIN_CONCRETE_STATUS_MS - elapsed
        setTimeout(() => pushThinkingStatus(trimmed), remaining)
        return
      }
      const updated = [...prev]
      updated[lastThinkingIdx] = {
        ...updated[lastThinkingIdx],
        label: trimmed,
        timestamp: Date.now(),
      }
      streamActivityRef.current = updated
      setStreamActivity(updated)
      return
    }

    // Before appending, guard against a fast-burst stack of concrete
    // statuses that the user can't read: if the tail is a concrete thinking
    // step that's been visible < 600ms, queue the new label.
    if (
      lastThinking
      && !isGenericThinkingLabel(lastThinking.label)
      && lastThinkingIdx === prev.length - 1
    ) {
      const elapsed = Date.now() - (lastThinking.timestamp || 0)
      if (elapsed < MIN_CONCRETE_STATUS_MS) {
        const remaining = MIN_CONCRETE_STATUS_MS - elapsed
        setTimeout(() => pushThinkingStatus(trimmed), remaining)
        return
      }
    }

    pushActivity(trimmed, 'thinking')
  }

  /** Clear the activity timeline */
  const clearActivity = useCallback(() => {
    streamActivityRef.current = []
    setStreamActivity([])
  }, [])

  return {
    streamActivity,
    streamActivityRef,
    setStreamActivity,
    pushActivity,
    pushThinkingStatus,
    clearActivity,
  }
}
